(() => {
  function hexToRgb(hex) {
    if (!hex) return [0, 0, 0];
    const value = hex.replace('#', '').trim();

    if (value.length === 3) {
      return [
        parseInt(value[0] + value[0], 16),
        parseInt(value[1] + value[1], 16),
        parseInt(value[2] + value[2], 16),
      ];
    }

    if (value.length === 6) {
      return [
        parseInt(value.slice(0, 2), 16),
        parseInt(value.slice(2, 4), 16),
        parseInt(value.slice(4, 6), 16),
      ];
    }

    return [0, 0, 0];
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
      img.src = src;
    });
  }

  function maxChannelDelta(r, g, b) {
    return Math.max(Math.abs(r - g), Math.abs(g - b), Math.abs(r - b));
  }

  function isNearWhite(rgb) {
    return rgb[0] > 242 && rgb[1] > 242 && rgb[2] > 242;
  }

  async function initBuilder(root) {
    const canvas = root.querySelector('[data-builder-canvas]');
    const status = root.querySelector('[data-builder-status]');

    const typeButtons = [...root.querySelectorAll('[data-builder-type]')];
    const designButtons = [...root.querySelectorAll('[data-builder-design]')];
    const podButtons = [...root.querySelectorAll('[data-builder-pod]')];
    const primaryInput = root.querySelector('[data-builder-primary-input]');
    const secondaryInput = root.querySelector('[data-builder-secondary-input]');

    const selectedList = root.querySelector('[data-builder-selected-list]');
    const selectedEmpty = root.querySelector('[data-builder-selected-empty]');

    if (!canvas || !status || !typeButtons.length) {
      return;
    }

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      status.textContent = 'Canvas is not supported in this browser.';
      return;
    }

    let primaryColor = root.dataset.primaryColor || '#111827';
    let secondaryColor = root.dataset.secondaryColor || '#475569';
    const maxWidth = Number(root.dataset.maxWidth || 1000);

    let activeType = 'desktop';
    let activeDesignButton = null;
    let activeBuffers = null;

    const selectedPods = new Map();

    function matchesType(candidate, currentType) {
      return candidate === currentType || candidate === 'both';
    }

    function setActiveButton(next, buttons) {
      buttons.forEach((button) => {
        const isActive = button === next;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
    }

    function setType(type) {
      activeType = type;
      setActiveButton(
        typeButtons.find((button) => button.dataset.type === type),
        typeButtons
      );

      designButtons.forEach((button) => {
        const visible = matchesType(button.dataset.type, activeType);
        button.hidden = !visible;
      });

      podButtons.forEach((button) => {
        const visible = matchesType(button.dataset.type, activeType);
        button.hidden = !visible;
      });

      const activeStillVisible = activeDesignButton && !activeDesignButton.hidden;
      if (!activeStillVisible) {
        const nextDesign = designButtons.find((button) => !button.hidden);
        if (nextDesign) {
          void activateDesign(nextDesign);
        } else {
          activeDesignButton = null;
          activeBuffers = null;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          status.textContent = `No ${type} designs configured.`;
        }
      } else {
        updateStatus();
      }

      renderSelectedPods();
    }

    async function buildBuffers(baseSrc, maskSrc) {
      const [baseImg, maskImg] = await Promise.all([
        loadImage(baseSrc),
        maskSrc ? loadImage(maskSrc) : Promise.resolve(null),
      ]);

      const scale = Math.min(1, maxWidth / baseImg.naturalWidth);
      const width = Math.max(1, Math.round(baseImg.naturalWidth * scale));
      const height = Math.max(1, Math.round(baseImg.naturalHeight * scale));

      canvas.width = width;
      canvas.height = height;

      const baseCanvas = document.createElement('canvas');
      baseCanvas.width = width;
      baseCanvas.height = height;
      const baseCtx = baseCanvas.getContext('2d', { willReadFrequently: true });

      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = width;
      maskCanvas.height = height;
      const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });

      if (!baseCtx || !maskCtx) {
        throw new Error('Unable to initialize image buffers.');
      }

      baseCtx.drawImage(baseImg, 0, 0, width, height);
      if (maskImg) {
        maskCtx.drawImage(maskImg, 0, 0, width, height);
      }

      return {
        baseData: baseCtx.getImageData(0, 0, width, height),
        maskData: maskImg ? maskCtx.getImageData(0, 0, width, height) : null,
        output: ctx.createImageData(width, height),
      };
    }

    function renderPreview() {
      if (!activeBuffers) return;

      const primary = hexToRgb(primaryColor);
      const secondary = hexToRgb(secondaryColor);
      const secondaryIsWhite = isNearWhite(secondary);

      const base = activeBuffers.baseData.data;
      const mask = activeBuffers.maskData ? activeBuffers.maskData.data : null;
      const output = activeBuffers.output.data;

      for (let i = 0; i < base.length; i += 4) {
        const alpha = base[i + 3] / 255;
        if (alpha <= 0) {
          output[i] = 0;
          output[i + 1] = 0;
          output[i + 2] = 0;
          output[i + 3] = 0;
          continue;
        }

        const red = base[i];
        const green = base[i + 1];
        const blue = base[i + 2];
        const luminosity = (red + green + blue) / 765;

        let pMask = 0;
        let sMask = 0;

        if (mask) {
          const mr = mask[i];
          const mg = mask[i + 1];
          const mb = mask[i + 2];
          const ma = mask[i + 3] / 255;

          const pRaw = Math.max(0, mr - Math.max(mg, mb));
          const sRaw = Math.max(0, mg - Math.max(mr, mb));

          pMask = pRaw >= 24 ? (pRaw / 255) * ma : 0;
          sMask = sRaw >= 24 ? (sRaw / 255) * ma : 0;
        } else {
          const delta = maxChannelDelta(red, green, blue);
          const isNeutral = delta < 18;
          const isTrimWhite = isNeutral && luminosity > 0.82;
          const isBodyGrey = isNeutral && luminosity > 0.18 && luminosity < 0.78;

          if (isTrimWhite && !secondaryIsWhite) {
            sMask = 1;
          } else if (isBodyGrey) {
            pMask = 1;
          }
        }

        const maskTotal = pMask + sMask;
        if (maskTotal > 1) {
          pMask /= maskTotal;
          sMask /= maskTotal;
        }

        const neutral = Math.max(0, 1 - Math.max(pMask, sMask));

        const pR = primary[0] * luminosity;
        const pG = primary[1] * luminosity;
        const pB = primary[2] * luminosity;
        const sR = secondary[0] * luminosity;
        const sG = secondary[1] * luminosity;
        const sB = secondary[2] * luminosity;

        output[i] = Math.round(red * neutral + pR * pMask + sR * sMask);
        output[i + 1] = Math.round(green * neutral + pG * pMask + sG * sMask);
        output[i + 2] = Math.round(blue * neutral + pB * pMask + sB * sMask);
        output[i + 3] = base[i + 3];
      }

      ctx.putImageData(activeBuffers.output, 0, 0);
    }

    function updateStatus() {
      const designLabel =
        activeDesignButton?.querySelector('.bittrak-builder__design-label')?.textContent?.trim() ||
        'No design selected';
      const typeLabel = activeType === 'rackmount' ? 'Rackmount' : 'Desktop';

      const selectedCount = [...selectedPods.values()].reduce((sum, item) => sum + item.qty, 0);
      const podText = selectedCount === 1 ? '1 pod selected' : `${selectedCount} pods selected`;

      status.textContent = `${typeLabel} / ${designLabel} - ${podText}`;
    }

    async function activateDesign(button) {
      const baseSrc = button.dataset.baseSrc || '';
      const maskSrc = button.dataset.maskSrc || '';

      if (!baseSrc) {
        status.textContent = 'Selected design is missing a base image.';
        return;
      }

      setActiveButton(button, designButtons.filter((candidate) => !candidate.hidden));
      activeDesignButton = button;
      status.textContent = 'Loading design...';

      try {
        activeBuffers = await buildBuffers(baseSrc, maskSrc);
        renderPreview();
        updateStatus();
      } catch (error) {
        status.textContent = error instanceof Error ? error.message : 'Unable to load selected design.';
      }
    }

    function renderSelectedPods() {
      if (!selectedList || !selectedEmpty) return;

      selectedList.innerHTML = '';
      const items = [...selectedPods.values()]
        .filter((item) => matchesType(item.type, activeType))
        .sort((a, b) => a.label.localeCompare(b.label));

      if (!items.length) {
        selectedEmpty.hidden = false;
        updateStatus();
        return;
      }

      selectedEmpty.hidden = true;

      items.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'bittrak-builder__selected-item';

        const text = document.createElement('div');
        text.className = 'bittrak-builder__selected-text';

        const label = document.createElement('p');
        label.className = 'bittrak-builder__selected-label';
        label.textContent = item.label;

        text.appendChild(label);

        if (item.code) {
          const code = document.createElement('p');
          code.className = 'bittrak-builder__selected-code';
          code.textContent = item.code;
          text.appendChild(code);
        }

        const qtyWrap = document.createElement('div');
        qtyWrap.className = 'bittrak-builder__selected-qty';

        const decrement = document.createElement('button');
        decrement.type = 'button';
        decrement.className = 'bittrak-builder__qty-btn';
        decrement.textContent = '-';
        decrement.setAttribute('aria-label', `Decrease ${item.label}`);
        decrement.addEventListener('click', () => {
          const current = selectedPods.get(item.id);
          if (!current) return;
          current.qty -= 1;
          if (current.qty <= 0) {
            selectedPods.delete(item.id);
          }
          renderSelectedPods();
        });

        const qtyValue = document.createElement('span');
        qtyValue.className = 'bittrak-builder__qty-value';
        qtyValue.textContent = String(item.qty);

        const increment = document.createElement('button');
        increment.type = 'button';
        increment.className = 'bittrak-builder__qty-btn';
        increment.textContent = '+';
        increment.setAttribute('aria-label', `Increase ${item.label}`);
        increment.addEventListener('click', () => {
          const current = selectedPods.get(item.id);
          if (!current) return;
          current.qty += 1;
          renderSelectedPods();
        });

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'bittrak-builder__remove-btn';
        remove.textContent = 'Remove';
        remove.setAttribute('aria-label', `Remove ${item.label}`);
        remove.addEventListener('click', () => {
          selectedPods.delete(item.id);
          renderSelectedPods();
        });

        qtyWrap.appendChild(decrement);
        qtyWrap.appendChild(qtyValue);
        qtyWrap.appendChild(increment);
        qtyWrap.appendChild(remove);

        li.appendChild(text);
        li.appendChild(qtyWrap);
        selectedList.appendChild(li);
      });

      updateStatus();
    }

    designButtons.forEach((button) => {
      button.addEventListener('click', () => {
        void activateDesign(button);
      });
    });

    typeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const nextType = button.dataset.type;
        if (!nextType || nextType === activeType) return;
        setType(nextType);
      });
    });

    podButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const podType = button.dataset.type || 'both';
        if (!matchesType(podType, activeType)) return;

        const podId = button.dataset.podId;
        if (!podId) return;

        const existing = selectedPods.get(podId);
        if (existing) {
          existing.qty += 1;
        } else {
          selectedPods.set(podId, {
            id: podId,
            label: button.dataset.label || 'Pod',
            code: button.dataset.code || '',
            type: podType,
            qty: 1,
          });
        }

        renderSelectedPods();
      });
    });

    if (primaryInput) {
      primaryInput.value = primaryColor;
      primaryInput.addEventListener('input', () => {
        primaryColor = primaryInput.value || primaryColor;
        renderPreview();
      });
    }

    if (secondaryInput) {
      secondaryInput.value = secondaryColor;
      secondaryInput.addEventListener('input', () => {
        secondaryColor = secondaryInput.value || secondaryColor;
        renderPreview();
      });
    }

    setType('desktop');
    renderSelectedPods();

    const firstVisibleDesign = designButtons.find((button) => !button.hidden);
    if (firstVisibleDesign) {
      void activateDesign(firstVisibleDesign);
    } else {
      status.textContent = 'Add at least one Desktop design block with a base image.';
    }
  }

  function boot(context) {
    const scope = context || document;
    const roots = scope.querySelectorAll('[data-bittrak-builder-root]');
    roots.forEach((root) => {
      initBuilder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) {
      boot(event.target);
    }
  });
})();
